import { Fieldset, Group, Stack } from "@mantine/core"
import { ReactNode } from "react"
import { DisabledReason, HelpButtonGroup } from "@dehub/theme"
import { DeliveryActionButton, DeliveryRowAction } from "../delivery/action-controls"
import { IPermissionCode, usePermission } from "../permission"

/**
 * Settings write actions are gated by the code the backend route checks (contract §1 «Тохиргооны табууд»):
 * BIZ_GOV_MNG for policy, COD, channel names, inbound flow, consolidation, holidays and templates;
 * LOG_SIZE_MNG for size scales; LOG_CLASS_RULE_MNG for classification rules. Without the code a control
 * stays visible, aria-disabled, with «Эрх хэрэгтэй: КОД».
 */
export const SETTINGS_WRITE = {
  governance: "BIZ_GOV_MNG",
  size: "LOG_SIZE_MNG",
  classification: "LOG_CLASS_RULE_MNG",
} as const satisfies Record<string, IPermissionCode>

export function useSettingsGate(code: IPermissionCode) {
  const perm = usePermission(code)
  return { allowed: perm.hasAccess, reason: perm.hasAccess ? null : perm.reason }
}

type RowActionProps = Readonly<{
  code: IPermissionCode
  label: string
  icon: ReactNode
  onClick: () => void
}>

/** Row action (grey, first «Үйлдэл» column) gated by a settings write code. */
export function SettingsRowAction({ code, label, icon, onClick }: RowActionProps) {
  const gate = useSettingsGate(code)
  return <DeliveryRowAction label={label} icon={icon} state={gate} onClick={onClick} />
}

/** Row of grey actions for the «Үйлдэл» cell. */
export function SettingsActionCell({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <Group gap="xs" wrap="nowrap">
      {children}
    </Group>
  )
}

type ButtonProps = Readonly<{
  code: IPermissionCode
  children: ReactNode
  onClick?: () => void
  loading?: boolean
  variant?: string
  leftSection?: ReactNode
  type?: "button" | "submit"
  /** @dehub/help `action` key: renders the attached «?» (R2). */
  helpKey?: string
  /** @dehub/help `reason` key for the popover while the write code is missing (R4); default `order.settings.reason.permission`. */
  reasonHelpKey?: string
  /** Reason code inside that row; default derived from the permission code. */
  reasonCode?: string
  /** data-help-id spotlight target. */
  helpId?: string
}>

/** Reason codes of `order.settings.reason.permission` by write code. */
const REASON_BY_CODE: Partial<Record<IPermissionCode, string>> = {
  BIZ_GOV_MNG: "governance",
  LOG_SIZE_MNG: "size",
  LOG_CLASS_RULE_MNG: "classification",
  BIZ_GOV_VIEW: "view",
}

/** Page-level write button (Шинэ, Хадгалах) gated by a settings write code. */
export function SettingsButton({ code, children, helpKey, reasonHelpKey = "order.settings.reason.permission", reasonCode, helpId, ...rest }: ButtonProps) {
  const gate = useSettingsGate(code)
  const button = (
    <DeliveryActionButton data-help-id={helpId} state={gate} color="dark" {...(rest as object)}>
      {children}
    </DeliveryActionButton>
  )
  if (!helpKey) return button
  // @dehub/help: attached «?» (R2) + reason popover while the write code is missing (R4).
  return (
    <DisabledReason helpKey={reasonHelpKey} reason={reasonCode ?? REASON_BY_CODE[code] ?? "view"} when={!gate.allowed} position="bottom-end">
      <HelpButtonGroup helpKey={helpKey}>{button}</HelpButtonGroup>
    </DisabledReason>
  )
}

type FieldsProps = Readonly<{
  code: IPermissionCode
  gap?: "xs" | "sm" | "md"
  children: ReactNode
}>

/**
 * The inputs of a settings form. Without the write code they are disabled (native fieldset),
 * not just the save button — the save button stays outside so its reason tooltip still shows.
 */
export function SettingsFields({ code, gap = "md", children }: FieldsProps) {
  const gate = useSettingsGate(code)
  return (
    <Fieldset variant="unstyled" disabled={!gate.allowed} p={0} m={0}>
      <Stack gap={gap}>{children}</Stack>
    </Fieldset>
  )
}
